/**
 * Profilni blok i dropdown meni korisnika — uloge, organizacija i obaveštenja.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import NotificationService from '@/shared/services/NotificationService';
import { scheduleNotificationCountRefresh } from '@/shared/utils/notificationCountSync';
import { EkonsultacijeButton } from '../../ui/Button';
import { getRoleLabel } from './useHeaderLogic';
import { getProfileIcon } from './ProfileTypeIcons';
import { isAdminRoleName, isPlatformAdmin, normalizePlatformRoles } from '@/shared/auth/obligationAuth';
import HeaderNotificationDropdownRow from './HeaderNotificationDropdownRow';
import { notificationsAtPage } from '@/shared/notifications/notificationPageUtils';
import {
  notificationsNeedOrganizationRegistry,
  useNotificationOrganizations,
} from '@/shared/notifications/useNotificationOrganizations';
import { useNotificationConsultationNotices } from '@/shared/notifications/useNotificationConsultationNotices';
import { useNotificationObligations } from '@/shared/notifications/useNotificationObligations';
import { useNotificationObligationProposals } from '@/shared/notifications/useNotificationObligationProposals';
import { useNotificationActionPlanCtaIds } from '@/shared/notifications/useNotificationActionPlanCtaIds';
import {
  normalizeUnreadNotificationsPayload,
} from '@/shared/notifications/notificationNormalize';
import { formatUnreadCount } from '@/shared/utils/pluralUtils';

const UserDropdown = ({
  user,
  isUserLoggedIn,
  activeRole,
  userType,
  selectedOrganizationName,
  displayName,
  unreadCount,
  onProfileBlur,
  onProfileLinkClick,
  onLogout,
  onSettingsClick,
  onRoleChange,
  onNavigateAdmin,
  onMenuListClick,
  profileBlockClassName,
  isProfileOpen,
  onModeratorAssignmentChanged,
  onUnreadCountSync,
}) => {
  const navigate = useNavigate();
  const notificationsBellRef = useRef(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [unreadList, setUnreadList] = useState([]);
  const [unreadLoading, setUnreadLoading] = useState(false);
  const [unreadError, setUnreadError] = useState(null);

  const [notificationPageIndex, setNotificationPageIndex] = useState(0);
  const [notificationSlideDirection, setNotificationSlideDirection] = useState(null);
  const notificationPageCount = unreadList.length;
  const currentUnreadNotification = unreadList[notificationPageIndex] ?? null;

  useEffect(() => {
    if (!notificationsOpen) {
      setNotificationPageIndex(0);
      setNotificationSlideDirection(null);
    }
  }, [notificationsOpen]);

  useEffect(() => {
    if (notificationPageIndex > 0 && notificationPageIndex >= notificationPageCount) {
      setNotificationPageIndex(Math.max(0, notificationPageCount - 1));
    }
  }, [notificationPageCount, notificationPageIndex]);

  const goPrevNotification = useCallback(() => {
    setNotificationSlideDirection('prev');
    setNotificationPageIndex((i) => Math.max(0, i - 1));
  }, []);

  const goNextNotification = useCallback(() => {
    setNotificationSlideDirection('next');
    setNotificationPageIndex((i) => Math.min(notificationPageCount - 1, i + 1));
  }, [notificationPageCount]);

  useEffect(() => {
    if (!notificationsOpen || !isUserLoggedIn) return undefined;
    let cancelled = false;
    setUnreadLoading(true);
    setUnreadError(null);
    NotificationService.search(false, { forceRefresh: true })
      .then((data) => {
        if (cancelled) return;
        const normalized = normalizeUnreadNotificationsPayload(data);
        setUnreadList(normalized);
        onUnreadCountSync?.(normalized.length);
      })
      .catch((err) => {
        if (!cancelled) {
          setUnreadList([]);
          setUnreadError(err?.message || 'Грешка при учитавању обавештења');
        }
      })
      .finally(() => {
        if (!cancelled) setUnreadLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [notificationsOpen, isUserLoggedIn, onUnreadCountSync]);

  useEffect(() => {
    if (!notificationsOpen) return undefined;

    let listenerAttached = false;
    const handleOutsidePointerDown = (event) => {
      const root = notificationsBellRef.current;
      if (!root || root.contains(event.target)) return;
      setNotificationsOpen(false);
    };

    const timerId = window.setTimeout(() => {
      document.addEventListener('pointerdown', handleOutsidePointerDown, true);
      listenerAttached = true;
    }, 0);

    return () => {
      clearTimeout(timerId);
      if (listenerAttached) {
        document.removeEventListener('pointerdown', handleOutsidePointerDown, true);
      }
    };
  }, [notificationsOpen]);

  useEffect(() => {
    if (!notificationsOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setNotificationsOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [notificationsOpen]);

  const toggleNotificationsDropdown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setNotificationsOpen((o) => !o);
  }, []);

  const closeNotificationsAndNavigate = useCallback(() => {
    setNotificationsOpen(false);
    onProfileLinkClick?.();
  }, [onProfileLinkClick]);

  const handleNotificationResolved = useCallback((notificationId) => {
    setUnreadList((prev) => prev.filter((x) => String(x.id) !== String(notificationId)));
    scheduleNotificationCountRefresh();
  }, []);

  const profileIcon = getProfileIcon(activeRole, userType, 16);
  const availableRoles = normalizePlatformRoles(user);
  const hasAdminRole = isPlatformAdmin(user);
  const primaryAdminRole = availableRoles.find((role) => isAdminRoleName(role)) || 'ADMIN';

  const visibleNotifications = useMemo(
    () => notificationsAtPage(unreadList, notificationPageIndex),
    [unreadList, notificationPageIndex],
  );

  const needsOrganizationRegistry = useMemo(
    () => notificationsNeedOrganizationRegistry(visibleNotifications),
    [visibleNotifications],
  );

  const notificationOrganizations = useNotificationOrganizations({
    enabled:
      isUserLoggedIn && notificationsOpen && hasAdminRole && needsOrganizationRegistry,
    notifications: visibleNotifications,
  });

  const notificationConsultations = useNotificationConsultationNotices({
    enabled: isUserLoggedIn && notificationsOpen && visibleNotifications.length > 0,
    notifications: visibleNotifications,
  });

  const notificationObligations = useNotificationObligations({
    enabled: isUserLoggedIn && notificationsOpen && visibleNotifications.length > 0,
    notifications: visibleNotifications,
  });

  const notificationObligationProposals = useNotificationObligationProposals({
    enabled: isUserLoggedIn && notificationsOpen && visibleNotifications.length > 0,
    notifications: visibleNotifications,
  });

  const actionPlanCtaIds = useNotificationActionPlanCtaIds({
    enabled: isUserLoggedIn && notificationsOpen && visibleNotifications.length > 0,
  });

  const draftActionPlanId =
    actionPlanCtaIds.draftActionPlanId ?? notificationObligationProposals.draftActionPlanId;

  const notificationContextLoading =
    notificationObligations.isLoading ||
    notificationObligationProposals.isLoading ||
    actionPlanCtaIds.isLoading;
  const switchToAdminRole = () => {
    if (hasAdminRole && !isAdminRoleName(activeRole)) {
      onRoleChange(primaryAdminRole);
    }
  };

  const handleAdminClick = () => {
    switchToAdminRole();
    navigate('/admin');
    onNavigateAdmin();
  };

  return (
    <div
      id="mojProfil"
      className={`${profileBlockClassName} header-profile-block${isProfileOpen ? ' open' : ''}`}
      onBlur={onProfileBlur}
    >
      <div className="profile-link-container">
        {isUserLoggedIn && (
          <>
            {/* The Admin panel link has been removed from here as requested */}
          </>
        )}
        <Link to="/profil" className="profile-link-button" onClick={onProfileLinkClick}>
          <span className="profile-link-icon">{profileIcon}</span>
          <span className="profile-link-label">{displayName}</span>
        </Link>
        {isUserLoggedIn && (
          <div
            className="header-notifications-bell-wrap"
            ref={notificationsBellRef}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className={`header-notifications-bell-btn${notificationsOpen ? ' header-notifications-bell-btn--open' : ''}`}
              aria-expanded={notificationsOpen}
              aria-haspopup="true"
              aria-label={
                unreadCount > 0
                  ? `Обавештења, ${formatUnreadCount(unreadCount)}`
                  : 'Обавештења, нема непрочитаних'
              }
              onClick={toggleNotificationsDropdown}
            >
              <svg
                className="header-notifications-bell-icon"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              {unreadCount > 0 ? (
                <span className="header-notifications-bell-badge">{unreadCount}</span>
              ) : null}
            </button>
            {notificationsOpen ? (
              <div
                className="header-notifications-dropdown"
                role="dialog"
                aria-labelledby="header-notifications-dropdown-title"
              >
                <div className="header-notifications-dropdown-head">
                  <h2
                    id="header-notifications-dropdown-title"
                    className="header-notifications-dropdown-title"
                  >
                    <i className="fas fa-bell header-notifications-dropdown-title-icon" aria-hidden />
                    Обавештења
                  </h2>
                  {unreadCount > 0 && !unreadLoading ? (
                    <span className="header-notifications-dropdown-head-meta">
                      {formatUnreadCount(unreadCount)}
                    </span>
                  ) : null}
                </div>
                <div className="header-notifications-dropdown-body">
                  {unreadLoading ? (
                    <div className="header-notifications-dropdown-placeholder">Учитавање…</div>
                  ) : unreadError ? (
                    <div className="header-notifications-dropdown-placeholder header-notifications-dropdown-placeholder--error">
                      {unreadError}
                    </div>
                  ) : unreadList.length === 0 ? (
                    <div className="header-notifications-dropdown-placeholder">
                      Нема непрочитаних обавештења
                    </div>
                  ) : currentUnreadNotification ? (
                    <div
                      key={currentUnreadNotification.id}
                      className={
                        notificationSlideDirection
                          ? `header-notifications-dropdown-slide header-notifications-dropdown-slide--${notificationSlideDirection}`
                          : 'header-notifications-dropdown-slide'
                      }
                      aria-live="polite"
                    >
                      {notificationContextLoading ? (
                        <div className="header-notifications-dropdown-placeholder">
                          Учитавање детаља…
                        </div>
                      ) : (
                        <HeaderNotificationDropdownRow
                          notification={currentUnreadNotification}
                          loginOrganizations={user?.loginData?.organizations ?? null}
                          obligations={notificationObligations.obligations}
                          obligationsInPreparation={notificationObligations.obligationsInPreparation}
                          organizations={notificationOrganizations.organizations}
                          organizationNameById={notificationOrganizations.organizationNameById}
                          consultationByDocumentId={
                            notificationConsultations.consultationByDocumentId
                          }
                          obligationProposalById={notificationObligationProposals.proposalById}
                          draftActionPlanId={draftActionPlanId}
                          implementationActionPlanId={actionPlanCtaIds.implementationActionPlanId}
                          completedActionPlanIds={actionPlanCtaIds.completedActionPlanIds}
                          extraObligations={notificationObligationProposals.obligations}
                          isAdmin={hasAdminRole}
                          onResolved={handleNotificationResolved}
                          onModeratorAssignmentChanged={onModeratorAssignmentChanged}
                          onLinkNavigate={closeNotificationsAndNavigate}
                        />
                      )}
                    </div>
                  ) : null}
                </div>
                {!unreadLoading && !unreadError && notificationPageCount > 0 ? (
                  <nav
                    className="header-notifications-dropdown-pagination"
                    aria-label="Навигација обавештењима"
                  >
                    <button
                      type="button"
                      className="header-notifications-dropdown-page-btn"
                      onClick={goPrevNotification}
                      disabled={notificationPageIndex <= 0}
                      aria-label="Претходно обавештење"
                    >
                      <i className="fas fa-chevron-left" aria-hidden />
                      <span>Претходно</span>
                    </button>
                    <span className="header-notifications-dropdown-page-indicator">
                      {notificationPageIndex + 1}
                      {' '}
                      од
                      {' '}
                      {notificationPageCount}
                    </span>
                    <button
                      type="button"
                      className="header-notifications-dropdown-page-btn"
                      onClick={goNextNotification}
                      disabled={notificationPageIndex >= notificationPageCount - 1}
                      aria-label="Следеће обавештење"
                    >
                      <span>Следеће</span>
                      <i className="fas fa-chevron-right" aria-hidden />
                    </button>
                  </nav>
                ) : null}
                <div className="header-notifications-dropdown-footer-row">
                  <Link
                    to="/profil?tab=notifications"
                    className="header-notifications-dropdown-footer"
                    onClick={closeNotificationsAndNavigate}
                  >
                    Прикажи сва обавештења
                  </Link>
                </div>
              </div>
            ) : null}
          </div>
        )}
        {isUserLoggedIn && (
          <>
            <div className="profile-divider" />
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onLogout();
              }}
              className="profile-logout-icon"
              aria-label="Одјави се"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              <span className="profile-logout-icon-label">Одјави се</span>
            </button>
          </>
        )}
      </div>
      {isUserLoggedIn && (
        <div className="profile-menu-popup" id="mojProfilUl">
          <div className="profile-menu-header">
            <div className="profile-user-info">
              {user?.loginData?.profile ? (
                <div className="profile-user-name">
                  {user.loginData.profile.first_name} {user.loginData.profile.last_name}
                </div>
              ) : user?.profile && (
                <div className="profile-user-name">
                  {user.profile['http://schema.id.rs/claims/givenname']}{' '}
                  {user.profile['http://schema.id.rs/claims/familyname']}
                </div>
              )}
              {availableRoles.length > 0 && (
                <div className="profile-user-roles">
                  {availableRoles.map((role) => (
                    <button
                      key={role}
                      type="button"
                      className={`role-button ${activeRole === role ? 'active' : ''}`}
                      onClick={() => onRoleChange(role)}
                    >
                      {getRoleLabel(role)}
                    </button>
                  ))}
                </div>
              )}
              {selectedOrganizationName && (
                <div className="profile-user-organizations">
                  <div className="organization-item">
                    <span className="org-name">{selectedOrganizationName}</span>
                  </div>
                </div>
              )}
              {!selectedOrganizationName && user?.loginData?.organizations && (
                <div className="profile-user-organizations">
                  {user.loginData.organizations.verified &&
                    user.loginData.organizations.verified.length > 0 && (
                      <div className="organization-item verified">
                        <span className="org-name">
                          {user.loginData.organizations.verified
                            .map((org) => org.name || org)
                            .join(', ')}
                        </span>
                        <span className="verified-badge">✓ Верификовано</span>
                      </div>
                    )}
                  {user.loginData.organizations.requested &&
                    user.loginData.organizations.requested.length > 0 && (
                      <div className="organization-item requested">
                        <span className="org-name">
                          {user.loginData.organizations.requested
                            .map((org) => org.name || org)
                            .join(', ')}
                        </span>
                        <span className="requested-badge">⏳ На чекању</span>
                      </div>
                    )}
                </div>
              )}
            </div>
          </div>
          {isAdminRoleName(activeRole) ? (
            <div className="profile-admin-button-container">
              <EkonsultacijeButton
                variant="primary"
                onClick={handleAdminClick}
                className="profile-admin-button"
              >
                Админ панел
              </EkonsultacijeButton>
            </div>
          ) : (
            <ul
              className="profile-menu-list"
              onClick={(e) => {
                if (
                  e.target.tagName === 'A' ||
                  e.target.closest('a') ||
                  e.target.tagName === 'LABEL'
                ) {
                  return;
                }
                onMenuListClick();
              }}
            >
              <li>
                <Link className="my-egov-link" to="/profil?tab=notifications">
                  Обавештења
                </Link>
              </li>
              <li>
                <button
                  type="button"
                  className="my-egov-link"
                  onClick={onSettingsClick}
                  style={{
                    background: 'none',
                    border: 'none',
                    font: 'inherit',
                    cursor: 'pointer',
                    padding: 0,
                    width: '100%',
                    textAlign: 'left',
                  }}
                >
                  <span className="long-string mt-2">Подешавања</span>
                </button>
              </li>
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default UserDropdown;
